import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { GameStatus, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { ANY_SPORT_TEAMS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import type { GenericGame } from 'components/seo/Games/Game';

import type { anyGameToGenericGame_anyGameInterface } from './__generated__/anyGame.graphql';

const sportPerGameType = {
  Game: Sport.FOOTBALL,
  GameOfBasketball: Sport.NBA,
  GameOfBaseball: Sport.BASEBALL,
};

export const anyGameToGenericGame = withFragments(
  ({
    anyGame,
    onClick,
  }: {
    anyGame: anyGameToGenericGame_anyGameInterface;
    onClick?: (id: string) => void;
  }): GenericGame => {
    const matchLiveOrPlayed = [
      GameStatus.played,
      GameStatus.playing,
    ].includesValue(anyGame.status);

    const sport = sportPerGameType[anyGame.__typename];

    const homeTeam = anyGame.homeTeam
      ? {
          ...anyGame.homeTeam,
          to: isType(anyGame.homeTeam, 'Club')
            ? generateSportPath(ANY_SPORT_TEAMS_SLUG, {
                sport,
                params: { slug: anyGame.homeTeam?.slug },
              })
            : undefined,
        }
      : null;

    const awayTeam = anyGame.awayTeam
      ? {
          ...anyGame.awayTeam,
          to: isType(anyGame.awayTeam, 'Club')
            ? generateSportPath(ANY_SPORT_TEAMS_SLUG, {
                sport,
                params: { slug: anyGame.awayTeam?.slug },
              })
            : undefined,
        }
      : null;

    return {
      id: anyGame.id,
      date: anyGame.date,
      gameweekDisplayName: anyGame.so5Fixture?.shortDisplayName || '',
      homeTeam,
      awayTeam,
      homeScore: anyGame.homeScore || 0,
      awayScore: anyGame.awayScore || 0,
      competition: {
        ...anyGame.competition,
        name: anyGame.competition.displayName,
      },
      status: anyGame.status,
      onClick: matchLiveOrPlayed ? () => onClick?.(anyGame.id) : undefined,
      coverageStatus: isType(anyGame, 'Game')
        ? anyGame.coverageStatus
        : undefined,
    };
  },
  {
    anyGameInterface: gql`
      fragment anyGameToGenericGame_anyGameInterface on AnyGameInterface {
        id
        date
        status: statusTyped
        competition {
          slug
          displayName
        }
        homeTeam {
          slug
          name
          pictureUrl
          styleGuideColor
        }
        homeScore
        awayTeam {
          slug
          name
          pictureUrl
          styleGuideColor
        }
        awayScore
        so5Fixture {
          slug
          shortDisplayName
        }
        ... on Game {
          id
          coverageStatus
        }
      }
    ` as TypedDocumentNode<anyGameToGenericGame_anyGameInterface>,
  }
);
